import { useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { PageFrame } from '@/components/layout/PageFrame'
import { fromHere, ORIGIN_MY_WORK, taskLinkState } from '@/components/process/taskLink'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui'
import { processErrorMessage, useProcessTasks } from '@/hooks/useProcess'
import { usePermission } from '@/hooks/usePermission'
import { usePermissionStore } from '@/stores/permissionStore'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import type { TaskScope } from '@/types/process'
import { DUE_WORD, bucketFromParam, groupByDue, type DueBucket } from './Home/dueBuckets'
import { tourAnchor } from '@/components/tour/tourAnchor'

/** mine | seat | all — `all` only for `tasks:read:all` holders. */
export function TaskScopeSwitch({ scope, onChange }: { scope: TaskScope; onChange: (scope: TaskScope) => void }) {
  const canReadAll = usePermission('tasks:read:all')
  const scopes: TaskScope[] = canReadAll ? ['mine', 'seat', 'all'] : ['mine', 'seat']
  return (
    <div className="flex gap-1 text-sm" {...tourAnchor('tasks.header.scope')}>
      {scopes.map((s) => (
        <button key={s} type="button" aria-pressed={scope === s} className={`rounded-md px-2 py-1 ${scope === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} onClick={() => onChange(s)}>{s}</button>
      ))}
    </div>
  )
}

/** The open task list, bucketed by due date through My work's own rule
 * (`groupByDue`, the deployment zone; SA-1174 design note D5), so a bar
 * segment on Home and the list it opens count the same tasks. `due` keeps
 * one bucket's group alone. Shared by `/tasks` and the record page's seat
 * view (`/orders?view=tasks`, E4 spec D24). */
export function TasksList({ scope, due = null }: { scope: TaskScope; due?: DueBucket | null }) {
  const tasks = useProcessTasks(scope, 'open')
  const location = useLocation()
  const zone = useDefaultTimezone()
  const groups = groupByDue(tasks.data?.tasks ?? [], new Date(), zone).filter((g) => !due || g.bucket === due)
  return (
    <>
      {tasks.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {tasks.isError && <p className="text-sm text-destructive">{processErrorMessage(tasks.error)}</p>}
      {tasks.data?.total === 0 && <p className="text-sm text-muted-foreground">Nothing waiting on you.</p>}
      {due && tasks.data && tasks.data.total > 0 && groups.length === 0 && (
        <p className="text-sm text-muted-foreground">{due === 'Overdue' ? 'Nothing overdue.' : due === 'No due date' ? 'Nothing without a due date.' : `Nothing due ${DUE_WORD[due]}.`}</p>
      )}
      {groups.map(({ bucket, tasks: list }) => (
        <section key={bucket} className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">{bucket}</h2>
          <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
            {list.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground">{t.order_number}</span>
                <Link to={`/tasks/${t.id}`} state={taskLinkState(fromHere(location, ORIGIN_MY_WORK))} className="flex-1 font-medium underline-offset-2 hover:underline">{t.step_number} {t.step_title}{t.record_title ? ` · ${t.record_title}` : ''}</Link>
                {t.seat_label && <Badge variant="neutral">{t.seat_label}</Badge>}
                {t.kind !== 'work' && <Badge variant={t.kind === 'attention' ? 'warning-soft' : 'neutral'}>{t.kind}</Badge>}
                <span className={`text-xs ${t.overdue ? 'text-destructive' : 'text-muted-foreground'}`}>{t.due_at ? new Date(t.due_at).toLocaleString() : 'no due date'}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  )
}

export function TasksPage() {
  const [params, setParams] = useSearchParams()
  // SA-475 (Home spec §13 FF6): "granted" = the store has LOADED and holds
  // the permission — `usePermission` answers true before the load. The URL's
  // ask is kept, so a permitted viewer's `?scope=all` bookmark lands on All
  // once the store loads; until then (and for anyone else) the page asks for
  // Mine. `?scope=` seeds the initial scope (spec D14), read once at mount.
  const granted = usePermissionStore((s) => s.isLoaded && s.permissions.has('tasks:read:all'))
  const seeded = params.get('scope')
  const [requested, setScope] = useState<TaskScope>(seeded === 'seat' ? 'seat' : seeded === 'all' ? 'all' : 'mine')
  const scope: TaskScope = requested === 'all' && !granted ? 'mine' : requested
  // SA-1174 (design note D11): `?due=` is read once at mount too; an unknown word is ignored.
  const [due, setDue] = useState<DueBucket | null>(() => bucketFromParam(params.get('due')))
  const clearDue = () => {
    setDue(null)
    const next = new URLSearchParams(params)
    next.delete('due')
    setParams(next, { replace: true })
  }
  return (
    <PageFrame width="list" className="space-y-4">
      <PageHeader title="My tasks" sticky actions={<TaskScopeSwitch scope={scope} onChange={setScope} />} />
      {due && (
        <div className="flex items-center gap-2 text-sm" data-testid="due-filter">
          <Badge variant="neutral">{due}</Badge>
          <button type="button" aria-label="Clear the due filter" className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={clearDue}>Clear</button>
        </div>
      )}
      <div className="space-y-4" {...tourAnchor('tasks.list.groups')}>
        <TasksList scope={scope} due={due} />
      </div>
    </PageFrame>
  )
}
